import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { formatCLP, hoyISO, claveMes, nombreMes, parseFecha } from '../lib/format';
import { q } from '../lib/vinculos';
import { resumenDia, balanceDelMes, sumarDias, gastoFijoMensual, promedioIngresoMensual, ingresosPorMes, diasDelMes } from '../lib/dia';
import ErrorBanner from './ErrorBanner';

const nombreDia = (fecha) => {
  const d = parseFecha(fecha);
  const texto = d.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'long' });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

function MiDia({ userId }) {
  const [movimientos, setMovimientos] = useState([]);
  const [gastosFijos, setGastosFijos] = useState([]);
  const [bolsillos, setBolsillos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fecha, setFecha] = useState(hoyISO());

  const loadData = useCallback(async () => {
    if (!userId) return;
    const [m, g, b] = await Promise.all([
      q(supabase.from('bolsillo_movimientos').select('*').eq('user_id', userId)),
      q(supabase.from('gastos_fijos').select('*').eq('user_id', userId)),
      q(supabase.from('bolsillos').select('id, nombre').eq('user_id', userId)),
    ]);
    setMovimientos(m || []);
    setGastosFijos(g || []);
    setBolsillos(b || []);
  }, [userId]);

  useEffect(() => {
    loadData().catch(e => setError('Error cargando datos: ' + e.message)).finally(() => setLoading(false));
  }, [loadData]);

  if (loading) return (
    <div className="bolsillos-loading"><div className="loading-spinner"></div><p>Cargando...</p></div>
  );

  const hoy = hoyISO();
  const clave = claveMes(fecha);
  const r = resumenDia({ movimientos, gastosFijos, fecha });
  const dias = balanceDelMes({ movimientos, gastosFijos, clave });
  const transcurridos = dias.filter(d => d.fecha <= hoy);
  const enVerde = transcurridos.filter(d => d.balance >= 0).length;
  const enRojo = transcurridos.length - enVerde;
  const sinIngresos = promedioIngresoMensual(movimientos, clave) === 0;
  const mesesIngreso = ingresosPorMes(movimientos, clave).filter(mm => mm.total > 0);

  // Cómo va el mes
  const ingresoMes = promedioIngresoMensual(movimientos, clave);
  const fijoMes = gastoFijoMensual(gastosFijos);
  const variablesMes = dias.reduce((s, d) => s + d.variables, 0);
  const fijoHastaHoy = (fijoMes / diasDelMes(clave)) * transcurridos.length;
  const quedaMes = ingresoMes - fijoMes - variablesMes;

  const nombreBolsillo = (id) => bolsillos.find(b => b.id === id)?.nombre;
  const alcanza = r.balance >= 0;
  const maxGasto = Math.max(...dias.map(d => d.gasto), 1);

  return (
    <div className="deudas-page">
      <div className="page-header">
        <div>
          <h2>Mi Día</h2>
          <p className="page-subtitle">Cuánto puedes gastar hoy, contando lo fijo que igual tienes que pagar</p>
        </div>
      </div>

      <ErrorBanner error={error} onClose={() => setError('')} />

      {/* Navegación por día */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <button className="btn-secondary" onClick={() => setFecha(sumarDias(fecha, -1))}>◀</button>
        <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={{ width: 'auto' }} />
        <button className="btn-secondary" onClick={() => setFecha(sumarDias(fecha, 1))}>▶</button>
        {fecha !== hoy && <button className="btn-secondary" onClick={() => setFecha(hoy)}>Hoy</button>}
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{nombreDia(fecha)}</span>
      </div>

      {sinIngresos && (
        <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: '0.88rem', color: '#92400e' }}>
          No hay ingresos registrados en los últimos 3 meses, así que no puedo calcular cuánto
          puedes gastar al día. Registra tus entradas de dinero en Mis Categorías y esto se llena solo.
        </div>
      )}

      {/* El número del día */}
      <div className="card panel" style={{ marginBottom: 16, borderLeft: `4px solid ${alcanza ? 'var(--success)' : 'var(--danger)'}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '2.5rem' }}>{alcanza ? '✅' : '⚠️'}</span>
          <div style={{ flex: 1, minWidth: 180 }}>
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              {alcanza ? 'Te queda para hoy' : 'Te pasaste por'}
            </p>
            <p style={{ margin: 0, fontWeight: 800, fontSize: '2rem', color: alcanza ? 'var(--success)' : 'var(--danger)' }}>
              {formatCLP(Math.abs(r.balance))}
            </p>
          </div>
        </div>
        <div style={{ display: 'grid', gap: 6, marginTop: 14, fontSize: '0.88rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span>Lo que ganas al día</span>
            <strong className="text-success">{formatCLP(r.ingresoDiario)}</strong>
          </div>
          {mesesIngreso.length > 0 && (
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: -4 }}>
              {mesesIngreso.length === 1
                ? `${nombreMes(mesesIngreso[0].clave)}: ${formatCLP(mesesIngreso[0].total)} ÷ ${r.dias} días`
                : `Promedio de ${mesesIngreso.length} meses (${mesesIngreso.map(mm => `${nombreMes(mm.clave).split(' ')[0]} ${formatCLP(mm.total)}`).join(', ')}) ÷ ${r.dias} días`}
            </span>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span>Gastos fijos del día <span style={{ color: 'var(--text-muted)' }}>(arriendo, luz, agua…)</span></span>
            <span className="text-danger">−{formatCLP(r.fijoDiario)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span>Lo que gastaste hoy</span>
            <span className="text-danger">−{formatCLP(r.totalPersonal)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
            <strong>Balance del día</strong>
            <strong className={alcanza ? 'text-success' : 'text-danger'}>{formatCLP(r.balance)}</strong>
          </div>
        </div>
      </div>

      {/* Gastos del día */}
      <div className="card panel" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: '1rem', marginTop: 0 }}>En qué se fue el día</h3>
        {r.personales.length === 0 ? (
          <p className="empty-text">Sin gastos registrados este día. Solo corre lo fijo: {formatCLP(r.fijoDiario)}.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {r.personales.map(mv => (
              <div key={mv.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: '0.9rem' }}>
                <span>
                  {mv.descripcion || 'Salida'}
                  <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {nombreBolsillo(mv.bolsillo_id) || 'Categoría eliminada'}{mv.rubro ? ` · ${mv.rubro}` : ''}
                  </span>
                </span>
                <strong className="text-danger">−{formatCLP(mv.monto)}</strong>
              </div>
            ))}
          </div>
        )}
        {r.empresa.length > 0 && (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '2px solid var(--border)' }}>
            <h4 style={{ fontSize: '0.85rem', margin: '0 0 6px', color: 'var(--text-secondary)' }}>
              Del negocio — {formatCLP(r.totalEmpresa)}
            </h4>
            <p className="field-hint" style={{ marginTop: 0 }}>
              Estos salen de lo que vende la empresa, así que no cuentan contra tu día.
            </p>
            {r.empresa.map(mv => (
              <div key={mv.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                <span>{mv.descripcion || 'Gasto de empresa'}</span>
                <span>−{formatCLP(mv.monto)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tira del mes */}
      <div className="card panel" style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: '1rem', marginTop: 0, marginBottom: 4 }}>{nombreMes(clave)}</h3>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 12px' }}>
          {enVerde} día{enVerde !== 1 ? 's' : ''} dentro de lo que ganas · {enRojo} pasado{enRojo !== 1 ? 's' : ''}
        </p>
        <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 70 }}>
          {dias.map(d => {
            const futuro = d.fecha > hoy;
            const alto = Math.max(4, Math.round((d.gasto / maxGasto) * 60));
            return (
              <button
                key={d.fecha}
                onClick={() => setFecha(d.fecha)}
                title={`${d.dia}: gastaste ${formatCLP(d.gasto)}`}
                style={{
                  flex: 1, height: alto, minWidth: 6, padding: 0, cursor: 'pointer',
                  border: d.fecha === fecha ? '2px solid var(--text)' : 'none',
                  borderRadius: 3,
                  background: futuro ? 'var(--border)' : d.balance >= 0 ? 'var(--success)' : 'var(--danger)',
                  opacity: futuro ? 0.5 : 1,
                }}
              />
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
          <span>1</span><span>{dias.length}</span>
        </div>
      </div>

      {/* Cómo va el mes */}
      <div className="card panel">
        <h3 style={{ fontSize: '1rem', marginTop: 0 }}>Cómo va el mes</h3>
        <div style={{ display: 'grid', gap: 6, fontSize: '0.88rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span>Lo que ganas en un mes <span style={{ color: 'var(--text-muted)' }}>({mesesIngreso.length === 1 ? 'según el mes cargado' : `promedio de ${mesesIngreso.length} meses`})</span></span>
            <strong className="text-success">{formatCLP(ingresoMes)}</strong>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span>Gastos fijos del mes <span style={{ color: 'var(--text-muted)' }}>(tu parte)</span></span>
            <span className="text-danger">−{formatCLP(fijoMes)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span>Gastos del mes hasta hoy</span>
            <span className="text-danger">−{formatCLP(variablesMes)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
            <strong>Te queda para el resto del mes</strong>
            <strong className={quedaMes >= 0 ? 'text-success' : 'text-danger'}>{formatCLP(quedaMes)}</strong>
          </div>
        </div>
        <p className="field-hint" style={{ marginBottom: 0 }}>
          De los gastos fijos se cuenta solo tu parte cuando están compartidos. Los gastos compartidos
          del día a día se cuentan completos: lo que te devuelven aparece en Me Deben.
          Llevas {formatCLP(fijoHastaHoy)} de gastos fijos corridos en los {transcurridos.length} días del mes.
        </p>
      </div>
    </div>
  );
}

export default MiDia;
